import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, ExternalLink, RefreshCw } from "lucide-react";
import api, { formatApiError } from "./api";
import { AdminLogin } from "./AdminLogin";
import { SubmissionsPanel } from "./SubmissionsPanel";
import { PortfolioPanel } from "./PortfolioPanel";
import { SettingsPanel } from "./SettingsPanel";
import { Toaster } from "@/components/ui/sonner";

const TABS = [
  { id: "estimates", label: "Estimates" },
  { id: "consultations", label: "Consultations" },
  { id: "callbacks", label: "Callbacks" },
  { id: "payment-notices", label: "Payment Notices" },
  { id: "messages", label: "Messages" },
  { id: "portfolio", label: "Portfolio" },
  { id: "settings", label: "Settings" },
];

const AdminDashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("estimates");
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats, tab]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    toast.success("Signed out");
    onLogout();
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <header className="border-b border-[#222222] px-6 md:px-10 py-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="bg-white px-2 py-1">
            <img src="/assets/logo.png" alt="Apex Home Renovation LLC" className="h-8 w-auto" />
          </span>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Admin Dashboard</p>
            <p className="text-xs text-gray-400" data-testid="admin-user-email">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-site-link"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-400 hover:text-[#D4AF37] transition-colors px-3 py-2">
            <ExternalLink className="h-4 w-4" /> View Site
          </a>
          <button onClick={logout} data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest border border-[#333333] hover:border-[#D4AF37] text-gray-300 hover:text-[#D4AF37] transition-colors px-4 py-2">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </header>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-[#222222] border-b border-[#222222]" data-testid="admin-stats-row">
          {TABS.filter((t) => t.id !== "settings").map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-stat-${t.id}`}
              className="bg-[#0A0A0A] hover:bg-[#111111] transition-colors p-4 text-left">
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{t.label}</p>
              <p className="font-serif text-2xl text-white mt-1">{stats[t.id]?.total ?? 0}</p>
              {stats[t.id]?.new > 0 && (
                <p className="text-[10px] text-[#D4AF37] mt-0.5">{stats[t.id].new} new</p>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="px-6 md:px-10 py-8 max-w-6xl">
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`text-[11px] uppercase tracking-[0.18em] px-4 py-2.5 border transition-colors duration-300 ${
                tab === t.id
                  ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                  : "border-[#333333] text-gray-400 hover:border-[#D4AF37]/50"
              }`}>
              {t.label}
            </button>
          ))}
          <button onClick={loadStats} className="ml-auto text-gray-500 hover:text-[#D4AF37] p-2 transition-colors"
            data-testid="admin-refresh-btn" aria-label="Refresh">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {tab === "portfolio" ? (
          <PortfolioPanel />
        ) : tab === "settings" ? (
          <SettingsPanel user={user} />
        ) : (
          <SubmissionsPanel key={tab} kind={tab} onChanged={loadStats} />
        )}
      </div>
    </div>
  );
};

export const AdminApp = () => {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = in

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      }
    })();
  }, []);

  return (
    <>
      <Toaster theme="dark" position="top-center" richColors />
      {user === null ? (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
        </div>
      ) : user === false ? (
        <AdminLogin onSuccess={setUser} />
      ) : (
        <AdminDashboard user={user} onLogout={() => setUser(false)} />
      )}
    </>
  );
};
